import React, { PureComponent } from "react";
import { PropTypes } from "prop-types";
import { Dropdown, Icon } from "semantic-ui-react";

import { AT_TYPES } from "../../constants/entity.js";
import { OPERATIONS } from "../../constants/operations.js";
import { getOperation, operationAvailability } from "../../utils/operations.js";

import LinkWithReset from "../utils/LinkWithReset.jsx";
import CopyString from "../utils/CopyString.jsx";

class ActionDropdownMenu extends PureComponent {
  static propTypes = {
    data: PropTypes.object.isRequired,
    editPath: PropTypes.string,
    createImagePath: PropTypes.string,
    cloneImagePath: PropTypes.string,
    viewPath: PropTypes.string,
    onDelete: PropTypes.func,
    copy: PropTypes.object.isRequired
  };

  // -- HANDLERS -- //

  handleDelete = () => {
    const { onDelete, data } = this.props;
    onDelete(data["@id"], data["@type"]);
  };

  // we want to prevent the dropdown from closing immediately if copy is clicked
  onCopy = evt => {
    evt.preventDefault();
    evt.stopPropagation();
  };

  // -- RENDER -- //

  // render id item dependent on available data.id
  renderId = () => {
    const { data } = this.props;
    if (data.id) {
      return (
        <Dropdown.Item onClick={this.onCopy}>
          <Icon name="id card outline" />
          <CopyString copyText={data.id} displayText={data.id} />
        </Dropdown.Item>
      );
    }

    return null;
  };

  // render settings item based on data type
  renderSettings = () => {
    const { data, copy } = this.props;

    if (!data || data["@type"] !== AT_TYPES.PROJECT) return null;

    return (
      <Dropdown.Item as={LinkWithReset} to={`${data["@id"]}/settings`}>
        <Icon name="cog" />
        {copy.settings}
      </Dropdown.Item>
    );
  };

  // render allow list item based on data type
  renderAllowlist = () => {
    const { data, copy } = this.props;

    if (!data || data["@type"] !== AT_TYPES.PROJECT) return null;

    // we currently don't have a view connection permission in this context
    // Using add conection as this is currently the same approacj we take for the images section
    const availability = operationAvailability(
      OPERATIONS.ADD_CONNECTION,
      data.operations,
      false
    );

    if (availability.available) {
      return (
        <Dropdown.Item as={LinkWithReset} to={`${data["@id"]}/allowlist`}>
          <Icon name="clipboard list" />
          {copy.allowlist}
        </Dropdown.Item>
      );
    }
  };

  // render users item based on data type
  renderUsers = () => {
    const { data, copy } = this.props;

    if (!data || data["@type"] !== AT_TYPES.PROJECT) return null;

    const availability = operationAvailability(
      OPERATIONS.LIST_USERS,
      data.operations,
      false
    );

    if (availability.available) {
      return (
        <Dropdown.Item as={LinkWithReset} to={`${data["@id"]}/users`}>
          <Icon name="user" />
          {copy.users}
        </Dropdown.Item>
      );
    }
  };

  // render edit item based on passed editPath
  renderEdit = () => {
    const { data, editPath, copy } = this.props;

    if (!data || !editPath) return null;

    const availability = operationAvailability(
      OPERATIONS.UPDATE,
      data.operations,
      false
    );

    if (availability.available) {
      return (
        <Dropdown.Item as={LinkWithReset} to={editPath}>
          <Icon name="edit" />
          {copy.edit}
        </Dropdown.Item>
      );
    }

    return null;
  };

  // render clone item based on passed editPath
  renderCloneImage = () => {
    const { data, cloneImagePath, copy } = this.props;

    if (!data || !cloneImagePath) return null;

    const availability = operationAvailability(
      OPERATIONS.CLONE_IMAGE,
      data.operations,
      false
    );

    if (availability.permitted) {
      return (
        <Dropdown.Item
          as={LinkWithReset}
          to={cloneImagePath}
          disabled={!availability.available}
        >
          <Icon name="clone outline" />
          {copy.clone_image}
        </Dropdown.Item>
      );
    }

    return null;
  };

  // render image item based on passed editPath
  renderCreateImage = () => {
    const { data, createImagePath, copy } = this.props;

    if (!data || !createImagePath) return null;

    const availability = operationAvailability(
      OPERATIONS.CREATE_IMAGE,
      data.operations,
      false
    );

    if (availability.permitted) {
      return (
        <Dropdown.Item
          as={LinkWithReset}
          to={createImagePath}
          disabled={!availability.available}
        >
          <Icon name="copy outline" />
          {copy.create_image}
        </Dropdown.Item>
      );
    }

    return null;
  };

  // render view item based on passed viewPath
  renderView = () => {
    const { viewPath, copy } = this.props;
    if (viewPath) {
      return (
        <Dropdown.Item as={LinkWithReset} to={viewPath}>
          <Icon name="file outline" />
          {copy.view}
        </Dropdown.Item>
      );
    }

    return null;
  };

  // render delete item based on flags, available operations and passed onDelete callback
  renderDelete = () => {
    const { data, onDelete, copy } = this.props;

    if (!data || !onDelete) return null;

    const availability = operationAvailability(
      OPERATIONS.DELETE,
      data.operations,
      false
    );

    if (availability.available) {
      return (
        <Dropdown.Item onClick={this.handleDelete}>
          <Icon name="trash" />
          {copy.delete}
        </Dropdown.Item>
      );
    }

    return null;
  };

  render() {
    return (
      <Dropdown
        icon="sidebar"
        direction="left"
        pointing="right"
        className="float-right"
      >
        <Dropdown.Menu className="entity-dropdown-menu">
          {this.renderId()}
          {this.renderCreateImage()}
          {this.renderCloneImage()}
          {this.renderAllowlist()}
          {this.renderUsers()}
          {this.renderSettings()}
          {this.renderView()}
          {this.renderEdit()}
          {this.renderDelete()}
        </Dropdown.Menu>
      </Dropdown>
    );
  }
}

export default ActionDropdownMenu;
